import React, { useState } from 'react';
import { AlertObject, AlertType } from '../../../../../../../components/Alert';
import { BulkActionButton } from '../BulkActionButton';
import { ModalBody } from './ModalBody';
import {
  MoveCandidateProps,
  MoveCandidateStageModal,
} from '../../../../../../JobPage/components/MoveCandidateStageModal';
import { BULK_ACTION_ERROR_MESSAGE } from '..';
import { checkIfSelectedCandidatesHaveSameStatus } from '../utils';
import { NewListCandidate } from '../../../../../../../entities/v1/applicant_tracking/NewListCandidate';
import { ApplicationService } from '../../../../../../../services/applicant_tracking/ApplicationService';
import { useBulkAction } from '../../../../../../../hooks/useBulkAction';

interface PropTypes {
  candidates: NewListCandidate[];
  selectedCandidates: NewListCandidate[];
  bulkSelectedIds: number[] | null;
  canBulkMoveToStage: boolean;
  selectionCountLabel: string;
  reloadPage: () => void;
  setAlert: (alert: AlertObject) => void;
}

function successAlertMessage(
  recordsSubmitted: number,
  recordsProcessed: number,
  selectionCountLabel: string,
  jobStageName: string,
) {
  return recordsProcessed === recordsSubmitted ? (
    <>
      <span className='fw-bold'>{selectionCountLabel}</span> were successfully
      moved to the <span className='fw-bold'>{jobStageName}</span> stage!
    </>
  ) : (
    <>
      <span className='fw-bold'>{recordsProcessed}</span> out of{' '}
      <span className='fw-bold'>{selectionCountLabel}</span> were successfully
      moved to the <span className='fw-bold'>{jobStageName}</span> stage. The
      remainder candidates are draft/rejected and cannot be moved.
    </>
  );
}

function actionStatusAndTooltipText(
  selectedCandidates: NewListCandidate[],
  multipleJobsSelected: boolean,
) {
  let toolTipText = 'Move to Stage';
  let actionDisabled: boolean = selectedCandidates.length === 0;

  const allRejected = selectedCandidates.every(
    (candidate) => candidate.application.status === 'rejected',
  );
  const sameStatusCandidatesSelected =
    checkIfSelectedCandidatesHaveSameStatus(selectedCandidates);

  if (allRejected) {
    toolTipText = 'Bulk move to stage is not available for rejected candidates';
    actionDisabled = true;
  } else if (multipleJobsSelected) {
    toolTipText =
      'Bulk move to stage is only available for candidates that share the same job';
    actionDisabled = true;
  } else if (!sameStatusCandidatesSelected) {
    toolTipText =
      'Bulk move to stage is only available for candidates that share the same status';
    actionDisabled = true;
  }

  return { actionDisabled, toolTipText };
}

export function MoveToStageButton(props: PropTypes) {
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const { progress, execute } = useBulkAction();
  const isBulkMode = props.bulkSelectedIds !== null;

  const selectedCandidateApplicationUniqueJobs = props.selectedCandidates
    .map((c) => c.application.job)
    .filter(
      (job, index, self) => self.findIndex((j) => job.id === j.id) === index,
    );

  const selectedJob = isBulkMode
    ? props.candidates[0]?.application?.job
    : selectedCandidateApplicationUniqueJobs[0];

  const { actionDisabled, toolTipText } = isBulkMode
    ? {
        actionDisabled:
          props.bulkSelectedIds.length === 0 || !props.canBulkMoveToStage,
        toolTipText: !props.canBulkMoveToStage
          ? 'Bulk move to stage requires exactly one job and one job stage filter applied'
          : 'Move to Stage',
      }
    : actionStatusAndTooltipText(
        props.selectedCandidates,
        selectedCandidateApplicationUniqueJobs.length > 1,
      );

  const onClosingMoveCandidateModal = () => {
    setIsModalOpen(false);
  };

  const onBulkMove = isBulkMode
    ? async (jobStageId: number) => {
        const failedCount = await execute(
          props.bulkSelectedIds,
          async (batchIds) => {
            await ApplicationService.bulkMoveStage({
              applicationIds: batchIds,
              jobStageId,
            });
          },
        );
        if (failedCount > 0) {
          throw new Error('Some moves could not be processed.');
        }
      }
    : undefined;

  const handleMoveStage = (moveProps: MoveCandidateProps) => {
    if (moveProps?.state === 'Success') {
      props.reloadPage();
      props.setAlert({
        message: isBulkMode ? (
          <>
            <span className='fw-bold'>{props.selectionCountLabel}</span> were
            successfully moved to the{' '}
            <span className='fw-bold'>{moveProps.jobStageName}</span> stage!
          </>
        ) : (
          successAlertMessage(
            props.selectedCandidates.length,
            moveProps.recordsProcessed,
            props.selectionCountLabel,
            moveProps.jobStageName,
          )
        ),
        type: AlertType.Success,
      });
    } else if (moveProps?.state === 'Error') {
      props.setAlert({
        message: BULK_ACTION_ERROR_MESSAGE,
        type: AlertType.Danger,
      });
    }
    setIsModalOpen(false);
  };

  return (
    <>
      <BulkActionButton
        iconName='bi-arrow-return-right'
        toolTipText={toolTipText}
        disabled={actionDisabled}
        onClick={() => setIsModalOpen(true)}
        analyticsId='bulk-move-to-stage-button'
      />
      <MoveCandidateStageModal
        applicationId={null}
        currentStage={null}
        candidateName={null}
        jobId={null}
        isOpen={isModalOpen && !!selectedJob}
        onClose={onClosingMoveCandidateModal}
        onMovingCandidate={handleMoveStage}
        customModalBody={
          <ModalBody
            selectedCandidates={props.selectedCandidates}
            selectionCountLabel={
              isBulkMode ? props.selectionCountLabel : undefined
            }
            job={selectedJob}
            warningDescription={
              isBulkMode
                ? `Applications will be moved regardless of their current status. Rejected and draft applications will be skipped.`
                : undefined
            }
            progress={progress}
            onBulkMove={onBulkMove}
            onClose={onClosingMoveCandidateModal}
            onMovingCandidate={handleMoveStage}
          />
        }
      />
    </>
  );
}
